import { ModuleRegistry } from "ag-grid-community";
import {
  AllCommunityModule,
  ExcelExportModule,
  ClientSideRowModelModule,
  ValidationModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  CellSelectionModule,
  ClipboardModule,
} from "ag-grid-enterprise";
import { DataService } from "../../../services/DataService";
import { useEffect, useState } from "react";
import { defaultExcelStyle } from "../../../utils/AgGridUtils/defaultExcelStyle";
import { DataTable } from "../../ui/DataTable";
import { ColumnBuilder } from "./ColumnBuilder";
import { useSaveOnCellChanged } from "./useSaveOnCellChanged";
import { _ROUTES } from "../../../utils/_routes_constants";
import { TOTAL_COST_RS_T } from "../quarrying-excavator/AgGridQuarryingExcavator";
import { _COLORS } from "../../../utils/constants";
import CustomGroupHeader from "../sales-filaire/CustomGroupHeader";

ModuleRegistry.registerModules([
  AllCommunityModule,
  ExcelExportModule,
  ClientSideRowModelModule,
  ValidationModule,
  RowGroupingModule,
  RowGroupingPanelModule,
  CellSelectionModule,
  ClipboardModule,
]);

export const SALES_AGG_SECTION_T = "Sales to AGG section (Tons)";
export const SELLING_PRICE_RS_T = "Selling Price (LRU to AGG section) Rs/T";
export const GROSS_PROFITS_RS_T = "Gross Profits Rs/T";
export const GROSS_PROFITS_P = "Gross Profits %";
export const TURNOVER_LRL_RS = "Turnover LRL Rs.";
export const SELLING_PRICE_INCREASE = "Selling Price Increase";
export const INTERNAL_SALES_VALUE = "Internal Sales - Value";

export const AgGridTurnoverLrl = ({ params, gridRef }) => {
  const dataService = new DataService();
  const [rowsData, setRowsData] = useState([]);
  const [pendingRefresh, setPendingRefresh] = useState(false);
  const [columns, setColumns] = useState([]);
  const excelStyles = defaultExcelStyle();

  const handleCellChange = useSaveOnCellChanged(
    params,
    setPendingRefresh,
    gridRef,
  );

  useEffect(() => {
    dataService.fetchData(_ROUTES.IPT_SALE_TURNOVER_LRL, params, (data) => {
      const transformedRows = data
        .map((d) =>
          d.subThemes.map((subTheme) => {
            const row = {
              theme: d.theme,
              subThemes: subTheme.name,
              rate: subTheme?.rate ?? 0,
              total: 0,
              inputs: {},
            };

            for (let i = 1; i <= 12; i++) {
              row[i] = null;
            }

            subTheme.inputs.forEach((input) => {
              row[input.fiscalMonth] = input.value;
              row.inputs[input.fiscalMonth] = input.id;
            });

            return row;
          }),
        )
        .flat();

      setRowsData(transformedRows);
    });

    dataService.fetchData(
      _ROUTES.IPT_QUARRYING_EXC,
      {
        site: params.site,
        fiscalYear: params.fiscalYear,
        "theme.name": TOTAL_COST_RS_T,
        version: params.version,
      },
      (grossProfitData) => {
        setColumns(ColumnBuilder({}, grossProfitData));
      },
    );
  }, [params.site, params, pendingRefresh]);

  const defaultColDef = {
    cellClassRules: {
      "blank-row": (params) => {
        return params.data?._SPACER;
      },
      "total-row": (params) =>
        params.node && params.node.aggData && params.node.footer,
      "total-header-col": (params) => {
        return params.colDef.field === "total";
      },
      "editable-cell": (params) => {
        return (
          (params.data?.theme === SELLING_PRICE_RS_T &&
            params.colDef.field === "rate") ||
          (params.data?.subThemes === SELLING_PRICE_INCREASE &&
            params.colDef.field !== "rate" &&
            params.colDef.field !== "total" &&
            params.colDef.field !== "subThemes")
        );
      },
      "diesel-cell": (params) => {
        return params.data?.subThemes === "Diesel Impact";
      },
    },
  };

  return (
    <>
      <DataTable
        rows={rowsData}
        columns={columns}
        excelStyles={excelStyles}
        onCellValueChanged={handleCellChange}
        fullHeight={true}
        rowHeight={30}
        fileNameCsvExport={"input-turnover-lrl"}
        gridRef={gridRef}
        agGridProps={{
          cellSelection: true,
          copyHeadersToClipboard: false,
          defaultColDef: defaultColDef,
          groupDisplayType: "groupRows",
          groupDefaultExpanded: -1,
          suppressAggFuncInHeader: true,
          groupRowRendererParams: {
            suppressCount: true,
          },
          groupTotalRow: (p) =>
            p.node?.key === TURNOVER_LRL_RS ? "bottom" : undefined,
          getRowClass: (params) => {
            if (!params.data) return "header-group-row";

            if (params.data?.subThemes === SALES_AGG_SECTION_T) {
              return "production-lrl-quarrying-cell";
            }

            return "";
          },
          groupRowRenderer: (node) => (
            <CustomGroupHeader
              node={node}
              style={{ color: _COLORS.groupTitleColor }}
              empty={node.value === SALES_AGG_SECTION_T}
            />
          ),
        }}
      />
    </>
  );
};
